// Function index:
//   mountJournalPanel(partyId) — wire the Journal button + overlay.
//       Lazy-loads existing notes on first open, debounces saves while
//       typing (1.5 s idle) and flushes on blur / close so a tab-close
//       doesn't drop the last edits.

import { getJournal, putJournal } from '../api.js';

export function mountJournalPanel(partyId: number): void {
  const btn = document.getElementById('journal-btn') as HTMLButtonElement;
  const overlay = document.getElementById('journal-overlay') as HTMLDivElement;
  const close = document.getElementById('journal-close') as HTMLButtonElement;
  const text = document.getElementById('journal-text') as HTMLTextAreaElement;
  const status = document.getElementById('journal-status') as HTMLSpanElement;

  let loaded = false;
  let dirty = false;
  let savingPromise: Promise<void> | null = null;
  let idleTimer: ReturnType<typeof setTimeout> | null = null;

  async function load(): Promise<void> {
    if (loaded) return;
    status.textContent = 'loading…';
    try {
      const r = await getJournal(partyId);
      text.value = r.content;
      status.textContent = '';
      loaded = true;
    } catch (err) {
      status.textContent = `load failed: ${(err as Error).message}`;
    }
  }

  async function flush(): Promise<void> {
    if (!dirty || savingPromise) return;
    status.textContent = 'saving…';
    const snapshot = text.value;
    savingPromise = (async () => {
      try {
        await putJournal(partyId, snapshot);
        // If the user typed more between request start and now, leave dirty
        // set so the next flush picks up the delta.
        if (text.value === snapshot) {
          dirty = false;
          status.textContent = 'saved';
          setTimeout(() => { if (status.textContent === 'saved') status.textContent = ''; }, 1500);
        }
      } catch (err) {
        status.textContent = `save failed: ${(err as Error).message}`;
      } finally {
        savingPromise = null;
      }
    })();
    await savingPromise;
  }

  btn.addEventListener('click', async () => {
    overlay.hidden = false;
    await load();
    text.focus();
  });
  close.addEventListener('click', async () => {
    if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
    await flush();
    overlay.hidden = true;
  });
  overlay.addEventListener('click', async (e) => {
    if (e.target === overlay) {
      if (idleTimer) { clearTimeout(idleTimer); idleTimer = null; }
      await flush();
      overlay.hidden = true;
    }
  });
  text.addEventListener('input', () => {
    dirty = true;
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => { idleTimer = null; void flush(); }, 1500);
  });
  text.addEventListener('blur', () => { void flush(); });
}
